import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Header } from '../components/header';
import { useAlert } from 'react-alert';
import MainService from '../services/Main.service';

const StartIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
);
const StopIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
);
const ClockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
);

const StatIcon = ({ type }) => {
    if (type === 'total') return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>;
    if (type === 'running') return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
    if (type === 'stopped') return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
    return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>;
};

const tableCustomStyles = {
    headRow: {
        style: {
            backgroundColor: '#f8fafc',
            borderBottomColor: '#e2e8f0',
            borderBottomWidth: '2px',
            minHeight: '44px',
        },
    },
    headCells: {
        style: {
            fontSize: '0.7rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#64748b',
            paddingLeft: '20px',
            paddingRight: '20px',
        },
    },
    cells: {
        style: {
            paddingLeft: '20px',
            paddingRight: '20px',
            fontSize: '0.875rem',
            color: '#0f172a',
        },
    },
    rows: {
        style: {
            minHeight: '54px',
            borderBottomColor: '#e2e8f0',
        },
        highlightOnHoverStyle: {
            backgroundColor: '#f8fafc',
            outline: 'none',
        },
    },
    pagination: {
        style: {
            borderTopColor: '#e2e8f0',
            color: '#64748b',
            fontSize: '0.8125rem',
        },
    },
};

function StatusBadge({ status }) {
    if (status === 1)  return <span className="status-badge running"><span className="badge-dot pulse" />Running</span>;
    if (status === 0)  return <span className="status-badge stopped"><span className="badge-dot" />Stopped</span>;
    return                    <span className="status-badge pending"><span className="badge-dot" />Pending</span>;
}

function ActionButton({ row, onAction }) {
    if (row.status === -1) {
        return (
            <button className="btn btn-pending" disabled>
                <ClockIcon style={{ width: 14, height: 14 }} />
                Pending
            </button>
        );
    }
    if (row.status === 1) {
        return (
            <button className="btn btn-stop" onClick={() => onAction(row)}>
                <StopIcon />
                Stop
            </button>
        );
    }
    return (
        <button className="btn btn-start" onClick={() => onAction(row)}>
            <StartIcon />
            Start
        </button>
    );
}

export default function HomePage() {
    const [serviceList, setServiceList] = useState([]);
    const [isLoading, setIsLoading]     = useState(true);
    const mainService                   = new MainService();
    const alert                         = useAlert();
    const intervalRef                   = useRef(null);

    const running = serviceList.filter(s => s.status === 1).length;
    const stopped = serviceList.filter(s => s.status === 0).length;
    const pending = serviceList.filter(s => s.status === -1).length;

    const columns = [
        {
            name: '#',
            selector: row => row.id,
            width: '70px',
            cell: row => <span className="text-mono" style={{ color: '#94a3b8' }}>#{row.id}</span>,
        },
        {
            name: 'Service Name',
            selector: row => row.serviceName,
            grow: 2,
            cell: row => (
                <span style={{ fontWeight: 500 }}>{row.serviceName}</span>
            ),
        },
        {
            name: 'Status',
            cell: row => <StatusBadge status={row.status} />,
            width: '140px',
        },
        {
            name: 'Action',
            cell: row => <ActionButton row={row} onAction={updateState} />,
            width: '140px',
        },
    ];

    useEffect(() => {
        getData();
        intervalRef.current = setInterval(getData, 15000);
        return () => clearInterval(intervalRef.current);
    }, []);

    async function getData() {
        await getAllServices('service');
        setIsLoading(false);
    }

    const updateState = async (item) => {
        try {
            const res = await mainService.updateState(item.type, item);
            if (res.data && res.data.message) {
                alert.show(res.data.message, { timeout: 2000, type: 'success' });
            }
            let counter = 0;
            const poll = setInterval(() => {
                if (counter < 4) { getData(); counter++; }
                else clearInterval(poll);
            }, 5000);
        } catch (err) {
            console.error(err);
            alert.show(err.message, { timeout: 2000, type: 'error' });
        }
    };

    const getAllServices = (type) => {
        mainService.getAllServices(type).then((res) => {
            if (res.data.body) {
                const data = res.data.body.map((d, i) => ({ ...d, id: i + 1, type }));
                setServiceList(data);
            }
        }).catch((err) => {
            console.error(err);
            alert.show(err.message, { timeout: 2000, type: 'error' });
        });
    };

    return (
        <Header title="Services" subtitle="Manage and monitor all services" onRefresh={getData}>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Total</span>
                        <span className="stat-card-icon total"><StatIcon type="total" /></span>
                    </div>
                    <div className="stat-card-value indigo">{serviceList.length}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Running</span>
                        <span className="stat-card-icon running"><StatIcon type="running" /></span>
                    </div>
                    <div className="stat-card-value green">{running}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Stopped</span>
                        <span className="stat-card-icon stopped"><StatIcon type="stopped" /></span>
                    </div>
                    <div className="stat-card-value red">{stopped}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Pending</span>
                        <span className="stat-card-icon pending"><StatIcon type="pending" /></span>
                    </div>
                    <div className="stat-card-value yellow">{pending}</div>
                </div>
            </div>

            <div className="table-card">
                <div className="table-toolbar">
                    <div className="table-toolbar-left">
                        <h3>All Services</h3>
                        <p>Auto-refreshes every 15 seconds</p>
                    </div>
                </div>
                <DataTable
                    columns={columns}
                    data={serviceList}
                    progressPending={isLoading}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    customStyles={tableCustomStyles}
                    highlightOnHover
                    noHeader
                />
            </div>
        </Header>
    );
}
